import { createContext, useContext, useMemo } from "react";
import {
  SubjectReportContextQuery,
  Sketch,
  SketchGeometryType,
  useSubjectReportContextQuery,
} from "../../generated/graphql";
import { MetricSubjectFragment } from "overlay-engine";

type SubjectReportContextData = {
  /**
   * Whether the sketch is a collection
   */
  isCollection: boolean;
  sketch: NonNullable<SubjectReportContextQuery["sketch"]>;
  childSketches: Pick<Sketch, "id" | "name" | "sketchClassId">[];
  siblingSketches: Pick<Sketch, "id" | "name" | "sketchClassId">[];
  relatedFragments: MetricSubjectFragment[];
};

export const SubjectReportContext = createContext<{
  data?: SubjectReportContextData;
  loading: boolean;
}>({ loading: true });

export function SubjectReportContextProvider({
  children,
  sketchId,
}: {
  children: React.ReactNode;
  sketchId: number;
}) {
  const { data, loading } = useSubjectReportContextQuery({
    variables: {
      sketchId: sketchId,
    },
  });
  const value = useMemo<SubjectReportContextData | undefined>(() => {
    if (data?.sketch?.sketchClass) {
      return {
        sketch: data.sketch,
        childSketches: data.sketch.children || [],
        siblingSketches: data.sketch.siblings || [],
        relatedFragments:
          (data.sketch.relatedFragments as MetricSubjectFragment[]) || [],
        isCollection:
          data.sketch.sketchClass.geometryType === SketchGeometryType.Collection,
      };
    }
    return undefined;
  }, [data]);

  return (
    <SubjectReportContext.Provider value={{ data: value, loading }}>
      {children}
    </SubjectReportContext.Provider>
  );
}

export function useSubjectReportContext() {
  const context = useContext(SubjectReportContext);
  return context;
}
